import { useMemo } from 'react';
import { useLanguage } from '@/common/hooks/useLanguageData';
import { THEME } from '@/common/styles/theme';
import {
	HOME_BANNER_DESKTOP_NATIVE_WIDTH,
	HOME_BANNER_MOBILE_NATIVE_WIDTH,
	HOME_BANNER_SLIDE_MS,
	HOME_BANNERS,
} from '../../config/homeBanners.config';
import { buildLoopedHomeBanners } from '../../utils/buildLoopedHomeBanners';
import { resolveHomeBanners } from '../../utils/resolveHomeBannerAssets';
import { useActiveBannerLayout } from './hooks/useActiveBannerLayout';
import { useHomeBannerCarousel } from './hooks/useHomeBannerCarousel';
import {
	BannerImage,
	BannerLink,
	BannerPicture,
	BannerRouterLink,
	BannerSection,
	BannerSlide,
	BannerTrack,
	BannerViewport,
} from './homeBannerCarousel.styles';

const MOBILE_MEDIA_QUERY = `(max-width: ${THEME.mobileBreakpoint})`;

const renderBannerMedia = (banner) => (
	<BannerPicture>
		<source media={MOBILE_MEDIA_QUERY} srcSet={banner.mobileSrc} />
		<BannerImage
			src={banner.desktopSrc}
			alt={banner.alt}
			loading="eager"
			fetchPriority="high"
			decoding="async"
			sizes={`(max-width: ${THEME.mobileBreakpoint}) min(100vw, ${HOME_BANNER_MOBILE_NATIVE_WIDTH}px), min(100vw, ${HOME_BANNER_DESKTOP_NATIVE_WIDTH}px)`}
		/>
	</BannerPicture>
);

const wrapBannerContent = (banner, content) => {
	if (!banner.href) {
		return content;
	}

	if (banner.href.startsWith('/')) {
		return <BannerRouterLink to={banner.href}>{content}</BannerRouterLink>;
	}

	return <BannerLink href={banner.href}>{content}</BannerLink>;
};

const HomeBannerCarousel = () => {
	const { lang } = useLanguage();
	const banners = useMemo(
		() => resolveHomeBanners(HOME_BANNERS, lang),
		[lang],
	);
	const loopedBanners = useMemo(
		() => buildLoopedHomeBanners(banners),
		[banners],
	);

	const {
		activeIndex,
		trackIndex,
		isTransitionEnabled,
		handleTrackTransitionEnd,
	} = useHomeBannerCarousel(banners.length, loopedBanners.length);
	const { aspectRatio } = useActiveBannerLayout(banners, activeIndex);
	const slideDurationMs = isTransitionEnabled ? HOME_BANNER_SLIDE_MS : 0;

	if (!banners.length) {
		return null;
	}

	return (
		<BannerSection aria-label="Promotions">
			<BannerViewport $aspectRatio={aspectRatio}>
				<BannerTrack
					$trackIndex={trackIndex}
					$durationMs={slideDurationMs}
					onTransitionEnd={handleTrackTransitionEnd}
				>
					{loopedBanners.map((item, index) => (
						<BannerSlide
							key={item.key}
							aria-hidden={index !== trackIndex}
						>
							{wrapBannerContent(
								item.banner,
								renderBannerMedia(item.banner),
							)}
						</BannerSlide>
					))}
				</BannerTrack>
			</BannerViewport>
		</BannerSection>
	);
};

export default HomeBannerCarousel;
